import { useEffect, useMemo, useState } from "react";
import { api, ApiError, DownloadLink, IssueResult, LicenseRow, Plan } from "../api/client";
import { useToast } from "../ui/toast";
import { PRODUCT_SHORT, productTitle } from "../products";
import { IconCopy, IconPlus, IconRefresh, IconSearch, IconX } from "../ui/icons";
import { DataTable, type Column } from "../ui/DataTable";
import { DAY, daysLeft, fmtDate, visualStatus } from "../ui/licenseStatus";

// Licence periods = the ones sold in the portal (Pricing: 1 / 3 / 12 months; backend PERIOD_DAYS 30 / 90 / 365).
const LICENSE_PERIODS: Array<{ days: number; label: string; short: string }> = [
  { days: 30, label: "1 miesiąc", short: "1 mies." },
  { days: 90, label: "3 miesiące", short: "3 mies." },
  { days: 365, label: "12 miesięcy", short: "12 mies." },
];

const licenseColumns: Column<LicenseRow>[] = [
  {
    key: "client", label: "Klient",
    get: (l) => l.clientRef || l.tid || "",
    filterValue: (l) => l.clientRef || "— bez opisu —",
    render: (l) => (
      <div>
        <div style={{ fontWeight: 550 }}>
          {l.clientRef || <span className="faint">— bez opisu —</span>}
          {(l.clientRef ?? "").toLowerCase().startsWith("portal-dev") &&
            <span className="badge st-expiring" style={{ marginLeft: 6 }}>DEV</span>}
        </div>
        <div className="faint mono" style={{ fontSize: 11 }}>{l.tid ?? "nieaktywowana"}</div>
      </div>
    ),
  },
  {
    key: "plan", label: "Produkt", get: (l) => PRODUCT_SHORT[l.plan] ?? l.plan,
    render: (l) => <span className={`badge ${l.plan === "governance" ? "st-active" : "st-issued"}`}>{PRODUCT_SHORT[l.plan] ?? l.plan}</span>,
  },
  { key: "seats", label: "Miejsca", align: "right", get: (l) => l.seats, render: (l) => <span className="tabular">{l.seats}</span> },
  {
    key: "installs", label: "Kopie", align: "right",
    get: (l) => (l.tid ? l.installs : -1),
    filterValue: (l) => (l.tid ? String(l.installs) : "—"),
    render: (l) => (
      <span className="tabular">
        {l.tid ? l.installs : "—"}
        {l.tid && l.installs > l.seats ? <span className="badge st-expiring" style={{ marginLeft: 6 }}>&gt; limit</span> : null}
      </span>
    ),
  },
  {
    key: "expiresAt", label: "Ważna do", align: "right", filter: "date",
    get: (l) => new Date(l.expiresAt).getTime(),
    render: (l) => {
      const dl = daysLeft(l.expiresAt);
      return <span className="tabular">{fmtDate(l.expiresAt)}<div className="faint" style={{ fontSize: 11 }}>{dl > 0 ? `za ${dl} dni` : "wygasła"}</div></span>;
    },
  },
  {
    key: "status", label: "Status",
    get: (l) => visualStatus(l).label,
    render: (l) => {
      const vs = visualStatus(l);
      return <span className={`badge st-${vs.key}`}><span className={`dot dot-${vs.key}`} />{vs.label}</span>;
    },
  },
];

export function Clients() {
  const [rows, setRows] = useState<LicenseRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<LicenseRow | null>(null);

  const load = () => {
    setRows(null);
    setError(null);
    api.listLicenses()
      .then((r) => setRows(r.licenses))
      .catch((e: ApiError) => setError(e.status === 403 ? "Brak dostępu (owner-only)." : e.message));
  };
  useEffect(load, []);

  const kpis = useMemo(() => {
    const r = rows ?? [];
    return {
      active: r.filter((l) => visualStatus(l).key === "active").length,
      expiring: r.filter((l) => visualStatus(l).key === "expiring").length,
      issued: r.filter((l) => l.status === "issued").length,
      revoked: r.filter((l) => l.status === "revoked").length,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const r = rows ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return r;
    return r.filter((l) => (l.clientRef ?? "").toLowerCase().includes(q) || (l.tid ?? "").includes(q));
  }, [rows, query]);

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        <Kpi label="Aktywne" value={kpis.active} />
        <Kpi label="Wygasające ≤30 dni" value={kpis.expiring} accent="medium" />
        <Kpi label="Wydane (nieaktywne)" value={kpis.issued} accent="accent" />
        <Kpi label="Cofnięte" value={kpis.revoked} accent="critical" />
      </div>

      <div className="row" style={{ marginBottom: 14 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <span style={{ position: "absolute", left: 11, top: 10, color: "var(--text-faint)" }}><IconSearch size={15} /></span>
          <input className="input" style={{ paddingLeft: 34 }} placeholder="Szukaj: firma / NIP / tenant…"
            value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="topbar-spacer" />
        <button className="btn" onClick={load}><IconRefresh size={15} /> Odśwież</button>
        <button className="btn primary" onClick={() => setShowModal(true)}><IconPlus size={15} /> Nowy klucz</button>
      </div>

      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : !rows ? (
        <div className="card"><div className="empty">Ładowanie…</div></div>
      ) : (
        <DataTable
          columns={licenseColumns}
          rows={filtered}
          rowKey={(l) => l.licenseId}
          onRowClick={(l) => setSelected(l)}
          selectedKey={selected?.licenseId ?? null}
          initialSort={{ key: "expiresAt", dir: "asc" }}
          empty={rows.length === 0 ? "Brak licencji. Kliknij „Nowy klucz”, aby wygenerować pierwszą." : "Brak wyników dla filtrów."}
        />
      )}

      {showModal && <NewKeyModal onClose={() => setShowModal(false)} onIssued={() => { setShowModal(false); load(); }} />}
      {selected && <ClientDrawer lic={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />}
    </>
  );

  function Kpi({ label, value, accent }: { label: string; value: number; accent?: string }) {
    const color = accent === "medium" ? "var(--medium)" : accent === "critical" ? "var(--critical)" : accent === "accent" ? "var(--accent-text)" : "var(--text)";
    return (
      <div className="card kpi">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value" style={{ color }}>{value}</div>
      </div>
    );
  }
}

// ---- New key modal ----------------------------------------------------------

function NewKeyModal({ onClose, onIssued }: { onClose: () => void; onIssued: () => void }) {
  const toast = useToast();
  const [plan, setPlan] = useState<Plan>("standard");
  const [seats, setSeats] = useState(3);
  const [durationDays, setDurationDays] = useState(365);
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [nip, setNip] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ issue: IssueResult; link: DownloadLink | null } | null>(null);

  const expiry = new Date(Date.now() + durationDays * DAY);

  const generate = async () => {
    setBusy(true);
    try {
      const clientRef = [company, email, nip].filter(Boolean).join(" · ") || undefined;
      const issue = await api.issue({ plan, seats, durationDays, clientRef });
      // Best effort: pair with a download link if an installer is published.
      const link = await api.downloadLink("stable").catch(() => null);
      setResult({ issue, link });
    } catch (e) {
      toast((e as ApiError).message || "Nie udało się wygenerować klucza");
    } finally {
      setBusy(false);
    }
  };

  const emailBlock = result
    ? [
        `Klucz licencyjny ${productTitle(result.issue.plan)} (${result.issue.seats} miejsc):`,
        ``,
        `    ${result.issue.key}`,
        ``,
        result.link ? `Instalator (${result.link.version}): ${result.link.url}` : `Instalator: (brak opublikowanego wydania)`,
        result.link ? `SHA-256: ${result.link.sha256}` : ``,
        ``,
        `Ważna do: ${fmtDate(result.issue.expiresAt)}.`,
        `Klucz jest nieaktywny do aktywacji w Twoim tenancie Microsoft 365.`,
      ].filter((l) => l !== undefined).join("\n")
    : "";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: 16 }}>{result ? "Klucz wygenerowany" : "Nowy klucz"}</h3>
          <div className="topbar-spacer" />
          <button className="btn icon sm ghost" onClick={onClose}><IconX size={16} /></button>
        </div>

        <div style={{ padding: 22 }}>
          {!result ? (
            <>
              <div className="field">
                <label className="label">Produkt</label>
                <div className="segmented">
                  <button className={plan === "standard" ? "active" : ""} onClick={() => setPlan("standard")}>{PRODUCT_SHORT.standard}</button>
                  <button className={plan === "governance" ? "active" : ""} onClick={() => setPlan("governance")}>{PRODUCT_SHORT.governance}</button>
                </div>
              </div>
              <div className="grid-2">
                <div className="field">
                  <label className="label">Liczba miejsc</label>
                  <input className="input" type="number" min={1} value={seats} onChange={(e) => setSeats(Math.max(1, Number(e.target.value)))} />
                </div>
                <div className="field">
                  <label className="label">Ważność</label>
                  <div className="segmented" style={{ width: "100%" }}>
                    {LICENSE_PERIODS.map((p) => (
                      <button key={p.days} className={durationDays === p.days ? "active" : ""} onClick={() => setDurationDays(p.days)}>{p.label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="faint" style={{ fontSize: 12, marginTop: -6, marginBottom: 14 }}>Ważna do: <b>{fmtDate(expiry.toISOString())}</b></p>
              <div className="grid-2">
                <div className="field"><label className="label">Firma</label><input className="input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="np. ACME sp. z o.o." /></div>
                <div className="field"><label className="label">E-mail</label><input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kontakt@acme.pl" /></div>
              </div>
              <div className="field"><label className="label">NIP (ewidencja)</label><input className="input" value={nip} onChange={(e) => setNip(e.target.value)} placeholder="000-000-00-00" /></div>
            </>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                Skopiuj poniższy blok i wyślij klientowi mailem. Klucz pokazujemy <b>tylko teraz</b> — backend trzyma jego hash.
              </p>
              <div className="result-block">{emailBlock}</div>
              <button className="btn primary" style={{ marginTop: 14, width: "100%" }}
                onClick={() => { navigator.clipboard.writeText(emailBlock); toast("Skopiowano blok do schowka"); }}>
                <IconCopy size={15} /> Kopiuj blok do maila
              </button>
            </>
          )}
        </div>

        <div className="row" style={{ padding: "16px 22px", borderTop: "1px solid var(--border)", justifyContent: "flex-end" }}>
          {!result ? (
            <>
              <button className="btn ghost" onClick={onClose}>Anuluj</button>
              <button className="btn primary" disabled={busy} onClick={generate}>{busy ? "Generuję…" : "Generuj klucz"}</button>
            </>
          ) : (
            <button className="btn primary" onClick={onIssued}>Gotowe</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Client drawer (renew / revoke) -----------------------------------------

function ClientDrawer({ lic, onClose, onChanged }: { lic: LicenseRow; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [plan, setPlan] = useState<Plan>(lic.plan);
  const [renewDays, setRenewDays] = useState(365);
  const renewPeriod = LICENSE_PERIODS.find((p) => p.days === renewDays) ?? LICENSE_PERIODS[2];
  const idArg = lic.tid ? { tid: lic.tid } : { licenseId: lic.licenseId };
  const vs = visualStatus(lic);

  const regenerate = async () => {
    setBusy(true);
    try {
      const res = await api.regenerateKey(idArg);
      setNewKey(res.key);
      setConfirmRegen(false);
      toast("Wygenerowano nowy klucz");
    } catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };

  const renew = async () => {
    setBusy(true);
    try { await api.renew({ ...idArg, durationDays: renewDays }); toast(`Odnowiono o ${renewPeriod.label}`); onChanged(); }
    catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };
  const savePlan = async () => {
    if (plan === lic.plan) return;
    setBusy(true);
    try { await api.changePlan({ ...idArg, plan }); toast(`Produkt zmieniony na ${PRODUCT_SHORT[plan]}`); onChanged(); }
    catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await api.revoke({ ...idArg, reason: "owner_revoked" }); toast("Licencja cofnięta"); onChanged(); }
    catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)" }}>
          <div>
            <h3 style={{ fontSize: 16 }}>{lic.clientRef || "Licencja"}</h3>
            <span className={`badge st-${vs.key}`} style={{ marginTop: 4 }}><span className={`dot dot-${vs.key}`} />{vs.label}</span>
          </div>
          <div className="topbar-spacer" />
          <button className="btn icon sm ghost" onClick={onClose}><IconX size={16} /></button>
        </div>

        <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
          <div className="field" style={{ marginBottom: 14 }}>
            <label className="label">Produkt</label>
            <div className="segmented">
              <button className={plan === "standard" ? "active" : ""} disabled={busy} onClick={() => setPlan("standard")}>{PRODUCT_SHORT.standard}</button>
              <button className={plan === "governance" ? "active" : ""} disabled={busy} onClick={() => setPlan("governance")}>{PRODUCT_SHORT.governance}</button>
            </div>
            {plan !== lic.plan && (
              <button className="btn primary sm" style={{ marginTop: 8 }} disabled={busy} onClick={savePlan}>
                Zmień produkt na {PRODUCT_SHORT[plan]}
              </button>
            )}
          </div>
          <Detail label="Miejsca" value={String(lic.seats)} />
          <Detail label="Aktywne kopie" value={lic.tid ? String(lic.installs) : "—"} />
          <Detail label="Tenant (tid)" value={lic.tid ?? "nieaktywowana"} mono />
          <Detail label="licenseId" value={lic.licenseId} mono />
          <Detail label="Wydana" value={fmtDate(lic.issuedAt)} />
          <Detail label="Ważna do" value={fmtDate(lic.expiresAt)} />
          <div className="field" style={{ marginTop: 14 }}>
            <label className="label">Okres odnowienia</label>
            <div className="segmented" style={{ width: "100%" }}>
              {LICENSE_PERIODS.map((p) => (
                <button key={p.days} className={renewDays === p.days ? "active" : ""} disabled={busy} onClick={() => setRenewDays(p.days)}>{p.label}</button>
              ))}
            </div>
          </div>

          {newKey && (
            <div style={{ marginTop: 8, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}>
              <div className="faint" style={{ fontSize: 11, marginBottom: 6 }}>
                NOWY KLUCZ — pokazany <b>tylko teraz</b>. Wyślij go klientowi; aplikacja poprosi o ponowną aktywację.
              </div>
              <div className="result-block" style={{ wordBreak: "break-all" }}>{newKey}</div>
              <button className="btn sm" style={{ marginTop: 8 }}
                onClick={() => { navigator.clipboard.writeText(newKey); toast("Skopiowano klucz"); }}>
                <IconCopy size={14} /> Kopiuj klucz
              </button>
            </div>
          )}
        </div>

        <div className="row" style={{ padding: "16px 22px", borderTop: "1px solid var(--border)" }}>
          <button className="btn" disabled={busy} onClick={renew}><IconRefresh size={15} /> Odnów (+{renewPeriod.short})</button>
          {!confirmRegen ? (
            <button className="btn" style={{ marginLeft: 8 }} disabled={busy} onClick={() => setConfirmRegen(true)}>Nowy klucz…</button>
          ) : (
            <button className="btn danger" style={{ marginLeft: 8 }} disabled={busy} onClick={regenerate}>Na pewno wygenerować?</button>
          )}
          <div className="topbar-spacer" />
          {!confirmRevoke ? (
            <button className="btn" disabled={busy || lic.status === "revoked"} onClick={() => setConfirmRevoke(true)}>Cofnij…</button>
          ) : (
            <button className="btn danger" disabled={busy} onClick={revoke}>Na pewno cofnąć?</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="faint" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      <div className={mono ? "mono" : ""} style={{ fontSize: 13, wordBreak: "break-all", textTransform: mono ? "none" : "capitalize" }}>{value}</div>
    </div>
  );
}
